import React from 'react';
import isEqual from 'lodash/isEqual';

import { FilterRange } from '@hcs/design-system';
import { FilterRangeType } from '@hcs/design-system';
import { QuickFilter } from '@hcs/design-system';
import { LoadingSpinner } from '@hcs/design-system';
import { PROPERTY_STATE_FIELD_CONFIGS } from '@hcs/property-state';
import { getPropertyStateFieldValue } from '@hcs/property-state';
import { PropertyStateFields } from '@hcs/types';
import { CompFiltersAll, CompsFiltersPaths } from '@hcs/types';
import {
  CompFilterChipTypeProps,
  CompFilterConfig,
  CompFilterControlTypeProps,
} from '@hcs/types';

import { useDocumentPatch, useReportPermissions } from '../../../hooks';
import { useCompsFiltersDocument } from '../../../hooks/useCompsFiltersDocument';
import { usePatchCompNumberRangeFilter } from '../../../hooks/usePatchCompNumberRangeFilter';
import { useSubjectDocument } from '../../../hooks/useSubjectDocument';
import {
  convertNumArrayToFilterRange,
  formatRangeYearBuilt,
  subjectDisplay,
} from '../../../utils';

import { CompFilterChip } from './CompFilterChip';

const currentYear = new Date().getFullYear();
const FIELD = PropertyStateFields.yearBuilt;
const PATH_FILTER: CompsFiltersPaths = `/data/filters/${FIELD}`;
const PATH_FIELD: CompsFiltersPaths = `${PATH_FILTER}/field`;
const PATH_REL: CompsFiltersPaths = `${PATH_FILTER}/relativeValue`;
const fieldConfig = PROPERTY_STATE_FIELD_CONFIGS[FIELD];
const dataHcName = 'comp-filter-year-built';
export const COMP_FILTER_YEAR_BUILT: CompFilterConfig = {
  Chip: ({ reportId, compType, ...props }: CompFilterChipTypeProps) => {
    const { data: filterDocument } = useCompsFiltersDocument(
      reportId,
      compType,
    );
    if (!filterDocument) return <span />;
    const filterValue = filterDocument.data.filters?.[FIELD];
    const active = !!filterValue;
    const label = active
      ? formatRangeYearBuilt(filterValue?.absoluteValue)
      : fieldConfig.labelShort;
    return (
      <CompFilterChip
        {...props}
        reportId={reportId}
        compType={compType}
        compField={FIELD}
        label={label}
      />
    );
  },
  Control: ({ reportId, compType }: CompFilterControlTypeProps) => {
    const documentPatchMutation = useDocumentPatch(reportId);
    const { mutate: patchFilter } = usePatchCompNumberRangeFilter(
      reportId,
      compType,
    );
    const { data: filterDocument } = useCompsFiltersDocument(
      reportId,
      compType,
    );
    const { data: subjectDocument } = useSubjectDocument(reportId);
    const { data: reportPermissions } = useReportPermissions(reportId);

    if (!filterDocument || !subjectDocument) {
      return <LoadingSpinner dataHcName={`${dataHcName}-skeleton`} />;
    }

    const filterValue = filterDocument.data.filters?.[FIELD];
    const subjectValue = getPropertyStateFieldValue(FIELD, {
      propertyStateType: 'Core',
      propertyState: subjectDocument.data.propertyState,
    });

    const handleChangeRelativeValue = (
      relativeValue: CompFiltersAll[PropertyStateFields.yearBuilt]['relativeValue'],
    ) => {
      documentPatchMutation.mutate({
        reportId,
        document: filterDocument,
        operations: [
          {
            op: 'add',
            path: PATH_REL,
            value: relativeValue,
          },
          {
            op: 'add',
            path: PATH_FIELD,
            value: FIELD,
          },
        ],
      });
    };

    return (
      <>
        {subjectValue && (
          <QuickFilter
            dataHcName={`${dataHcName}-quick-filter`}
            disabled={!reportPermissions?.isEditable}
            buttons={[
              {
                label: '+/- 5yrs',
                active: isEqual([-5, 5], filterValue?.relativeValue),
                dataHcName: `${dataHcName}-quick-filter-btn-0`,
                onClick: () => handleChangeRelativeValue([-5, 5]),
              },
              {
                label: '+/- 15yrs',
                active: isEqual([-15, 15], filterValue?.relativeValue),
                dataHcName: `${dataHcName}-quick-filter-btn-1`,
                onClick: () => handleChangeRelativeValue([-15, 15]),
              },
              {
                label: '+/- 25yrs',
                active: isEqual([-25, 25], filterValue?.relativeValue),
                dataHcName: `${dataHcName}-quick-filter-btn-2`,
                onClick: () => handleChangeRelativeValue([-25, 25]),
              },
              {
                label: '+/- 35yrs',
                active: isEqual([-35, 35], filterValue?.relativeValue),
                dataHcName: `${dataHcName}-quick-filter-btn-3`,
                onClick: () => handleChangeRelativeValue([-35, 35]),
              },
            ]}
          />
        )}
        <FilterRange
          maxVal={currentYear}
          disabled={!reportPermissions?.isEditable}
          initialValue={convertNumArrayToFilterRange(
            filterValue?.absoluteValue,
          )}
          subjectDisplay={subjectDisplay(
            false,
            convertNumArrayToFilterRange(filterValue?.relativeValue),
          )}
          onBlur={(value: FilterRangeType) =>
            patchFilter?.absoluteValue(FIELD, value)
          }
          dataHcName={`${dataHcName}-bed`}
          extraValidators={[
            (value: FilterRangeType) => {
              const currentYear = new Date().getFullYear();
              if (
                (value.max && parseInt(value.max) > currentYear) ||
                (value.min && parseInt(value.min) > currentYear)
              ) {
                return `Cannot set a year greater than ${currentYear}.`;
              }
              return undefined;
            },
          ]}
        />
      </>
    );
  },
};
